import { useRef, KeyboardEvent, ChangeEvent } from "react";

const TokenInput = ({ value, onChange }: TokenInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const newChar = e.target.value.slice(-1);

    if (!/^\d*$/.test(newChar)) return;

    const newValue = value.split("");
    newValue[index] = newChar;
    const updatedValue = newValue.join("");
    onChange(updatedValue);

    // Move focus to next input if available
    if (newChar && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newValue = value.split("");
      newValue[index] = "";
      onChange(newValue.join(""));

      // Move focus to previous input if available
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d*$/.test(pastedData)) return;
    onChange(pastedData);

    // Focus last filled input or first empty input
    const focusIndex = Math.min(pastedData.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="flex justify-center gap-2">
      {Array(6)
        .fill(0)
        .map((_, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            value={value[index] || ""}
            ref={(el) => (inputRefs.current[index] = el)}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className={`w-12 h-12 text-center text-xl border border-gray-3 rounded-lg outline-none ${
              value[index]
                ? "ring-1 ring-gray-5"
                : "focus:ring-1 focus:ring-gray-5 focus:border-gray-5"
            }`}
          />
        ))}
    </div>
  );
};

export default TokenInput;

interface TokenInputProps {
  value: string;
  onChange: (value: string) => void;
}
